import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { sanitize } from '@/utils/sanitize';
import { messageForError } from '@/utils/userMessage';
import { CustomLogo, FactoryProduct, OrderDetail, ProductRosterDetail, Roster, LogoHistory } from '@/models';
import { filter } from 'lodash';
import { computed, ComputedRef, h, reactive, ref, VNode } from 'vue';
import {
  can,
  getCurrentDatetime,
  getFileExtensionType,
  logError,
  openV2Application,
  urlToBase64,
} from '.';
import { showToast } from './swal';
import {
  makeReorderMessage as sharedMakeReorderMessage,
  type ReorderFactoryProduct,
} from '@/utils/reorderMessage';

interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  sortKey?: string;
  renderer?: (value: any, row: any) => string | VNode | number;
}

export type RosterQuantityGuard = (
  row: ProductRosterDetail,
  newValue: string | number,
  product: FactoryProduct,
  previousValue: string | number,
) => boolean;

export const useProductDetails = (
  props: any,
  rosterGuards?: { onBeforeRosterQuantityChange?: RosterQuantityGuard | null },
) => {
  // const fontFile = ref<any[]>([])
  const factoryProduct = ref<any>(null);
  const product_numbers = ref<any[]>([]);
  const product_texts = ref<any[]>([]);
  const custom_logos = ref<any[]>([]);
  const logo_selected_id = ref<any>(null);
  const isEditing = ref(false);
  const backupData = ref([]);
  const productSizes = ref([]);
  const measurement_unit = reactive({
    unit: 'cm',
  });
  const production_file_info = ref<Record<string, null | number | string>>({
    width: null,
    height: null,
  });
  const loading = ref<boolean>(false);
  const showLogoHistoryDialog = ref(false)
  const computedProducts: ComputedRef<FactoryProduct[]> = computed(() => props.factoryProducts);

  // Helper to get size name from ID
  const getSizeName = (size) => {
    const found = productSizes.value.find((s) => s.name === size);
    return found ? found.name : size;
  };
  let roster_headers: Column[] = [
    {
      key: 'size',
      label: 'Size',
      renderer: (val: any, row: ProductRosterDetail) => {
        return isEditing.value
          ? h(
              !factoryProduct.value.is_custom_product && productSizes.value.length > 0 ? 'select' : 'input',
              {
                class:
                  'w-full rounded-lg border border-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
                value: row.size ?? '',
                onInput: (e: any) => {
                  row.size = e.target.value;
                },
                onChange: (e: any) => {
                  row.size = e.target.value;
                },
              },
              productSizes.value.length > 0
                ? productSizes.value.map((s) => h('option', { value: s.name }, s.name))
                : undefined
            )
          : h('div', row?.size ?? 'N/A');
      },
    },

    {
      key: 'quantity',
      label: 'Quantity',
      renderer: (val: any, row: ProductRosterDetail) =>
        isEditing.value
          ? h('input', {
              type: 'number',
              class:
                'w-full rounded-lg border border-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
              value: row.quantity,
              onInput: (e: any) => {
                const prev = row.quantity;
                const raw = (e?.target as HTMLInputElement | undefined)?.value;
                if (rosterGuards?.onBeforeRosterQuantityChange) {
                  if (
                    !rosterGuards.onBeforeRosterQuantityChange(
                      row,
                      raw,
                      factoryProduct.value,
                      prev,
                    )
                  ) {
                    (e?.target as HTMLInputElement).value = String(prev ?? '');
                    return;
                  }
                }
                row.quantity = raw;
              },
            })
          : h('div', row?.quantity ?? 'N/A'),
    },
    {
      key: 'number',
      label: 'No.',
      renderer: (val: any, row: ProductRosterDetail) =>
        isEditing.value
          ? h('input', {
              type: 'text',
              class:
                'w-full rounded-lg border border-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
              value: row.number,
              onInput: (e: any) => (row.number = e.target.value),
            })
          : h('div', row?.number ?? 'N/A'),
    },
    {
      key: 'text',
      label: 'Text',
      renderer: (val: any, row: ProductRosterDetail) =>
        isEditing.value
          ? h('input', {
              type: 'text',
              class:
                'w-full rounded-lg border border-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
              value: row.text,
              onInput: (e: any) => (row.text = e.target.value),
            })
          : h('div', row?.text ?? 'N/A'),
    },
    {
      key: 'captains_associates',
      label: 'C/A',
      renderer: (val: any, row: ProductRosterDetail) =>
        isEditing.value
          ? h(
              'select',
              {
                class:
                  'w-full rounded-lg border border-line px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
                onChange: (e: any) => (row.captains_associates = e.target.value),
              },
              [
                h('option', { value: '', selected: (row.captains_associates ?? '') === '' }, '-'),
                h(
                  'option',
                  { value: 'captains', selected: row.captains_associates === 'captains' },
                  'C'
                ),
                h(
                  'option',
                  { value: 'associates', selected: row.captains_associates === 'associates' },
                  'A'
                ),
              ]
            )
          : h(
              'div',
              row?.captains_associates === 'captains'
                ? 'C'
                : row?.captains_associates === 'associates'
                  ? 'A'
                  : 'N/A'
            ),
    },
    // [AB#6498] Country flags: per-row flag, only meaningful for products with a
    // flag-type logo placement configured — the column always renders (matching
    // the rest of this fixed roster_headers array), values just stay 'N/A' for
    // products with no flag placements.
    {
      key: 'flag',
      label: 'Flag',
      renderer: (val: any, row: ProductRosterDetail) =>
        isEditing.value
          ? h('input', {
              type: 'text',
              maxlength: 2,
              class:
                'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500',
              value: row.flag ?? '',
              onInput: (e: any) => {
                row.flag = String(e.target.value).toUpperCase();
              },
            })
          : h('div', row?.flag || 'N/A'),
    },
  ];

  let custom_text_headers: Column[] = [
    { key: 'label', label: 'Label' },
    { key: 'placement', label: 'Placement' },
    { key: 'color_pantone', label: 'Pantone' },
    { key: 'font_family', label: 'Font Family' },
  ];
  let logo_history_headers: Column[] = [
    { key: 'company_name', label: 'Merchant' },
    {
      key: 'order_no',
      label: 'Order No',
      sortable: false,
      renderer: (val: any, row: any) => row.order_no ?? 'Order not Approved yet.',
    },
  ];
  let custom_logos_headers: Column[] = [
    {
      key: '',
      label: 'Logo',
      renderer: (value: any, row: CustomLogo) => {
        return row.url
          ? h('img', {
              src: `${storage_url}/${row.url}`,
              alt: 'Logo',
              style: { width: '50px', height: '50px' },
            })
          : '';
      },
    },
    {
      key: '',
      label: 'Side',
      renderer: (value: any, row: CustomLogo) => {
        return row.id ? row.side : '';
      },
    },
    {
      key: 'size',
      label: 'Size',
      renderer: (value: any, row: CustomLogo) => {
        if (measurement_unit.unit === 'cm') {
          return row.originalWidth ? `${row.originalWidth}cm x ${row.originalHeight}cm` : '';
        } else if (measurement_unit.unit === 'in') {
          return `${parseFloat(row.originalWidth).toFixed(2)} X
                      ${parseFloat(row.originalHeight).toFixed(2)}`;
        } else {
          return 'N/A';
        }
      },
    },

    {
      key: 'download',
      label: 'Download',
      renderer: (value: any, row: CustomLogo) => {
        const buttons = [];
        const btnClasses =
          'px-2 py-2 flex gap-2 justify-between items-center rounded-lg text-sm capitalize text-ink-soft bg-surface-2';

        if (row.id) {
          if (row.id !== logo_selected_id.value) {
            buttons.push(
              h(
                'button',
                {
                  class: btnClasses,
                  onClick: () => downloadLogo(row.id, row.url),
                },
                'Download'
              )
            );
          } else {
            buttons.push(
              h('button', { class: btnClasses }, [h('span', { class: 'spinner-border' })])
            );
          }

          if (can('vectorize-logo') && getFileExtensionType('raster', row.original_logo_url)) {
            const vInfo = row.vectorized_logo_info;

            // Vectorize loader
            if (vInfo.show_vectorized_loader) {
              buttons.push(
                h('button', { class: btnClasses }, [h('span', { class: 'spinner-border btn' })])
              );
            } else if (vInfo.vectorized_file_url) {
              buttons.push(
                h(
                  'button',
                  {
                    class: btnClasses,
                    onClick: () => downloadVectorizedFile(row.logo_index, row),
                  },
                  'Download Vectorized Logo'
                )
              );
            } else {
              buttons.push(
                h(
                  'button',
                  {
                    class: btnClasses,
                    onClick: () => vectorizeLogo({ item: row }, false, factoryProduct.value),
                  },
                  'Vectorize Logo'
                )
              );
            }

            // Preview loader
            if (vInfo.show_preview_loader) {
              buttons.push(
                h('button', { class: btnClasses }, [h('span', { class: 'spinner-border btn' })])
              );
            } else if (vInfo.preview_file_url) {
              buttons.push(
                h(
                  'button',
                  {
                    class: btnClasses,
                    onClick: () => downloadVectorizedFile(row.logo_index, row, true),
                  },
                  'Download Vectorized Logo Preview'
                )
              );
            } else {
              buttons.push(
                h(
                  'button',
                  {
                    class: btnClasses,
                    onClick: () => vectorizeLogo({ item: row }, true, factoryProduct.value),
                  },
                  'Preview Vectorized Logo'
                )
              );
            }
          }
          if (row.id !== logo_selected_id.value) {
            buttons.push(
              h(
                'button',
                {
                  class: btnClasses,
                  onClick: () => handleShowLogoHistory(row.id, row.url),
                },
                'Logo History'
              )
            );
          } 
        }

        return h('div', { class: 'flex gap-2 flex-wrap' }, buttons);
      },
    },
  ];
  let svg_group_headers: Column[] = [
    { key: 'id', label: 'Group' },
    { key: 'pantone', label: 'Pantone' },
    { key: 'name', label: 'Pantone name' },
  ];

  const API_URL = import.meta.env.VITE_APP_BASE_URL;
  const storage_url = import.meta.env.VITE_APP_FILE_URL;
  const batch_loading = ref<boolean>(false);
  const loadingPreviewImages = ref<boolean>(false);
  const front_preview = ref<string>(null);
  const back_preview = ref<string>(null);
  // Reversible products (AB#19678): inside-face previews, null for non-reversible
  const inside_front_preview = ref<string>(null);
  const inside_back_preview = ref<string>(null);
  const production_content = ref<string>(null);
  const roster_detail = ref<Roster[]>([]);
  const svg_pattern_last_value_y = ref(5000);
  const logo_pattern_last_value_y = ref(0);
  const INCH_TO_CENTIMETER = 2.54;
  const logoHistoryLoading = ref(false)
  const logoHistoryData = ref<LogoHistory[]>([])

  const batchDownloadAssets = (product, item = null, activity_index = null) => {
    let factory_product = null;
    if (item && activity_index !== null && activity_index !== -1) {
      let items = props.order.items[item.order_item_index];
      let activity_item = item.activity_item_data[activity_index];
      factory_product = items.factory_products.find((factory_product) => {
        return factory_product.id === activity_item.factory_product_id;
      });
    } else {
      factory_product = product;
    }
    batch_loading.value = true;
    let zip_file_element = document.createElement('a');
    zip_file_element.setAttribute(
      'href',
      `${API_URL}/order/${props.order.id}/product/${factory_product.id}/extract-zip`
    );
    let zip_file_name = `order_${getCurrentDatetime()}_product_${factory_product.product_name}.xlsx`;
    if (props.order.order_no) {
      zip_file_name = `order_${props.order.order_no}_product_${factory_product.product_name}.xlsx`;
    }
    zip_file_element.setAttribute('download', zip_file_name);
    zip_file_element.style.display = 'none';
    zip_file_element.setAttribute('id', `${Math.random()}`);
    document.body.appendChild(zip_file_element);
    zip_file_element.click();
    setTimeout(() => {
      batch_loading.value = false;
    }, 5000);
  };
  const downloadVectorizedFile = (logo_index, custom_logo, preview_logo = false) => {
    const vectorized_logo_info = custom_logo?.vectorized_logo_info;
    let file_url = vectorized_logo_info?.vectorized_file_url;
    let file_name = vectorized_logo_info?.vectorized_file_name;
    let file_extension = vectorized_logo_info?.vectorized_file_extension;
    if (preview_logo) {
      file_url = vectorized_logo_info?.preview_file_url;
      file_name = vectorized_logo_info?.preview_file_name;
      file_extension = vectorized_logo_info?.preview_file_extension;
    }
    const file_name_with_extension = `${file_name}.${file_extension}`;
    file_url = `${storage_url}/${file_url}`;
    if (preview_logo) {
      custom_logo.vectorized_logo_info.show_preview_loader = true;
    } else {
      custom_logo.vectorized_logo_info.show_vectorized_loader = true;
    }
    urlToBase64(file_url)
      .then((base64Content: string) => {
        const downloadLink = document.createElement('a');
        downloadLink.href = base64Content;
        downloadLink.download = file_name_with_extension; // Set the filename here
        downloadLink.style.display = 'none';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        if (preview_logo) {
          custom_logo.vectorized_logo_info.show_preview_loader = false;
        } else {
          custom_logo.vectorized_logo_info.show_vectorized_loader = false;
        }
      })
      .catch((errorResponse) => {
        if (preview_logo) {
          custom_logo.vectorized_logo_info.show_preview_loader = false;
        } else {
          custom_logo.vectorized_logo_info.show_vectorized_loader = false;
        }
        handleResponseException(errorResponse);
      });
  };

  const downloadImages = async (factory_product) => {
    loadingPreviewImages.value = true;
    let urls = [];
    let imageNames = [];

    // Determine image names
    const timestamp = getCurrentDatetime();
    const baseName = props.order.order_no || timestamp;
    
      // Use front_image and back_image from the factory_product
      if (factory_product.front_image) {
        urls.push({ url: factory_product.front_image });
        imageNames.push(`${baseName}-front-preview.png`);
      }
      if (factory_product.back_image) {
        urls.push({ url: factory_product.back_image });
        imageNames.push(`${baseName}-back-preview.png`);
      }
      if (factory_product.inside_front_image) {
        urls.push({ url: factory_product.inside_front_image });
        imageNames.push(`${baseName}-inside-front-preview.png`);
      }
      if (factory_product.inside_back_image) {
        urls.push({ url: factory_product.inside_back_image });
        imageNames.push(`${baseName}-inside-back-preview.png`);
      }
      if (urls.length === 0) {
        loadingPreviewImages.value = false;
        showToast('This product has no preview images.', 'error');
        return;
      }

    try {
      const response = await http.post('convert-image-to-base64', {
        urls,
      });

      // Download each image with a small delay between them
      for (let i = 0; i < response.data.image_urls.length; i++) {
        const link = document.createElement('a');
        link.href = response.data.image_urls[i].url;
        link.download = imageNames[i];
        link.click();

        // Add a small delay between downloads
        if (i < response.data.image_urls.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      loadingPreviewImages.value = false;
      showToast('Images downloaded.', 'success');
    } catch (error) {
      loadingPreviewImages.value = false;
      showToast(messageForError(error, "The images couldn't be downloaded. Try again."), 'error');
      console.error('Download error:', error);
    }
  };

  const showPreview = (activity_item) => {
    front_preview.value = `${activity_item.activity_files[0].url}`;
    back_preview.value = `${activity_item.activity_files[1].url}`;
    // Activity files are the admin-uploaded front/back pair only
    inside_front_preview.value = null;
    inside_back_preview.value = null;
    // $modal.show('product-preview')
  };

  const downloadProductionFile = (path, filename) => {
    // Create a new link
    const anchor = document.createElement('a');
    let url = `${import.meta.env.VITE_APP_BASE_URL}/get-svg?path=${path}&file_name=${filename}`;
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = 'none';
    anchor.setAttribute('id', `${Math.random()}`);
    // Append to the DOM
    document.body.appendChild(anchor);
    anchor.click();
    // Remove element from DOM
    document.body.removeChild(anchor);
  };
  const parseSvgString = async (
    svg_string: string,
    factory_product: any,
    // font_file: any[],
    storage_url: string
  ) => {
    if (!svg_string || svg_string.lastIndexOf('</g>') === -1) return 'No SVG Exists';

    let production_content = '';
    let roster_detail = [];
    let custom_logos_texts_string = '';
    let companies_name: any[] = [];

    const custom_texts = factory_product.product_custom_texts;
    const roster_text_index = (custom) =>
      factory_product.product_roster_detail.findIndex(
        (roaster) => roaster.text === custom.value
      ) !== -1;
    const roster_number_index = (custom) =>
      factory_product.product_roster_detail.findIndex(
        (roaster) => roaster.number === custom.value
      ) !== -1;
    const custom_name_index = custom_texts.findIndex(
      (custom) => custom.type === 'name' && custom.value && roster_text_index(custom)
    );

    const custom_number_index = custom_texts.findIndex(
      (custom) => custom.type === 'number' && custom.value && roster_number_index(custom)
    );

    const sorted_custom_text_svgs = [];
    const custom_text_svgs = factory_product.custom_text_svgs ?? [];

    if (custom_name_index !== -1) {
      sorted_custom_text_svgs.push({
        svg: custom_text_svgs[custom_name_index],
        type: 'name',
      });
    }
    if (custom_number_index !== -1) {
      sorted_custom_text_svgs.push({
        svg: custom_text_svgs[custom_number_index],
        type: 'number',
      });
    }

    custom_text_svgs.forEach((svg, index) => {
      if (index !== custom_name_index && index !== custom_number_index) {
        sorted_custom_text_svgs.push({ svg, type: 'common' });
      }
    });

    for (const roster of factory_product.product_roster_detail) {
      let text = '',
        number = '',
        name_height = '',
        number_height = '';
      companies_name = [];

      for (const custom_text_svg of sorted_custom_text_svgs) {
        const element = await getDocFromString(custom_text_svg.svg);
        const el = element?.querySelector('g');

        if (custom_text_svg.type === 'name' && el) {
          const tspan = el.querySelector('tspan');
          if (tspan) {
            tspan.innerHTML = sanitize(roster.text);
            tspan.setAttribute('x', '0');
            tspan.setAttribute('y', '0');
            text = new XMLSerializer().serializeToString(el.querySelector('text'));
            name_height = custom_texts[custom_name_index].originalHeight;
          }
        } else if (custom_text_svg.type === 'number' && el) {
          const tspan = el.querySelector('tspan');
          if (tspan) {
            tspan.innerHTML = sanitize(roster.number);
            tspan.setAttribute('x', '0');
            tspan.setAttribute('y', '0');
            number = new XMLSerializer().serializeToString(el.querySelector('text'));
            number_height = custom_texts[custom_number_index].originalHeight;
          }
        } else if (el) {
          const tspan = el.querySelector('tspan');
          if (tspan) {
            tspan.setAttribute('x', '0');
            tspan.setAttribute('y', '0');
            companies_name.push({
              name: new XMLSerializer().serializeToString(el.querySelector('text')),
              size: 'For ALL',
            });
          }
          custom_logos_texts_string += new XMLSerializer().serializeToString(el);
        }
      }

      roster_detail.push({
        text,
        number,
        size: roster.size,
        name_height,
        number_height,
      });
    }

    for (const company of companies_name) {
      roster_detail.push({
        text: company.name,
        number: null,
        size: company.size,
        name_height: null,
        number_height: null,
      });
    }

    for (let custom_logo_svg of factory_product.custom_logo_svgs) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(custom_logo_svg, 'image/svg+xml');
      const img = doc.querySelector('img');
      const logo_url = img?.getAttribute('xlink:href');

      if (logo_url) {
        try {
          const base64_logo = await urlToBase64(logo_url);
          custom_logo_svg = custom_logo_svg.replace(logo_url, base64_logo);
        } catch (error) {
          console.error(error);
        }
      }

      custom_logos_texts_string += custom_logo_svg;
    }

    if (custom_logos_texts_string) {
      svg_string = svg_string.substring(0, svg_string.lastIndexOf('</g>'));
      svg_string += `${getSVGPattern(roster_detail, factory_product.measurement_ratio)}\n`;

      if (
        !(factory_product.custom_logos.length === 1 && factory_product.custom_logos[0].url === null)
      ) {
        svg_string += await getLogoPattern(
          factory_product.custom_logos,
          factory_product.measurement_ratio
        );
      }

      svg_string += '\n</g>\n</svg>';
    }

    let svg_doc = await getDocFromString(svg_string);
    let svgElem = svg_doc.querySelector('svg');

    production_file_info.value = {
      width: svgElem.getAttribute('width'),
      height: svgElem.getAttribute('height'),
    };
    const svgGroups = factory_product.svg_groups;

    svgGroups.forEach((group) => {
      const elems = svg_doc.querySelectorAll(`[id][fill]`);
      elems.forEach((el) => {
        let doc_elem_id = el.getAttribute('id');
        if (!doc_elem_id) return;
        doc_elem_id = doc_elem_id.includes('_') ? doc_elem_id.split('_')[0] : doc_elem_id;
        if (doc_elem_id.toLowerCase() === group.id.toLowerCase()) {
          el.setAttribute('fill', group.color);
        }
      });
    });

    // Add Fonts
    // if (font_file.length > 0) {
    //     const styleTag = document.createElementNS(
    //         "http://www.w3.org/2000/svg",
    //         "style",
    //     );
    //     font_file.forEach((font) => {
    //         styleTag.innerHTML += `@font-face { font-family: ${font.file_name}; src: url('${font.font_url}'); }`;
    //     });
    //     svgElem?.prepend(styleTag);
    // }

    // Add Back Image
    const backGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    backGroup.setAttribute(
      'transform',
      `matrix(1 0 0 1 ${parseFloat(production_file_info.value.width.toString())} 0)`
    );
    const backImage = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    backImage.setAttributeNS(
      'http://www.w3.org/1999/xlink',
      'href',
      `${storage_url}/${factory_product.back_image}`
    );
    backImage.setAttribute(
      'height',
      `${parseFloat(production_file_info.value.height.toString()) / 2}px`
    );
    backImage.setAttribute(
      'width',
      `${parseFloat(production_file_info.value.height.toString()) / 2}px`
    );
    // [AB#22852] No back image (custom products): don't add an <image> pointing at /null.
    if (factory_product.back_image) backGroup.appendChild(backImage);
    svg_doc.querySelector('g')?.prepend(backGroup);

    // Add Front Image
    const frontGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    frontGroup.setAttribute(
      'transform',
      `matrix(1 0 0 1 ${parseFloat(production_file_info.value.width.toString()).toString()} ${(parseFloat(production_file_info.value.width.toString()) / 2 + 500).toString()})`
    );
    const frontImage = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    frontImage.setAttributeNS(
      'http://www.w3.org/1999/xlink',
      'href',
      `${storage_url}/${factory_product.front_image}`
    );
    frontImage.setAttribute(
      'height',
      `${parseFloat(production_file_info.value.height.toString()) / 2}px`
    );
    frontImage.setAttribute(
      'width',
      `${parseFloat(production_file_info.value.height.toString()) / 2}px`
    );
    if (factory_product.front_image) frontGroup.appendChild(frontImage);
    svg_doc.querySelector('g')?.prepend(frontGroup);

    // Set SVG Dimensions and ViewBox
    svgElem?.setAttribute(
      'width',
      String(parseFloat(production_file_info.value.width.toString()) * 2 * 0.1044) + 'px'
    );
    svgElem?.setAttribute(
      'height',
      String(parseFloat(production_file_info.value.height.toString()) * 0.1044) + 'px'
    );

    const viewBox = svgElem?.getAttribute('viewBox')?.split(' ') ?? ['0', '0', '0', '0'];
    svgElem?.setAttribute(
      'viewBox',
      `${viewBox[0]} ${viewBox[1]} ${parseFloat(production_file_info.value.width.toString()) * 2} ${viewBox[3]}`
    );

    production_content = new XMLSerializer().serializeToString(svg_doc);
    return production_content;
  };
  const downloadLogo = (id, url) => {
    let logo_element = document.createElement('a');
    logo_selected_id.value = id;
    loading.value = true;
    http
      .get(`file/logo-download/${id}`)
      .then((response) => {
        let logo = response.data.logo;
        let file_path = response.data.path;
        if (logo && file_path) {
          const d = new Date();
          let time = d.getTime();
          let url_split_array = file_path.split('/');
          let file_name = url_split_array[url_split_array.length - 1];
          logo_element.setAttribute('id', `${time}`);
          logo_element.setAttribute('href', logo);
          logo_element.setAttribute('download', `${file_name}`);
          logo_element.style.display = 'none';
          document.body.appendChild(logo_element);
          logo_element.click();
          document.body.removeChild(logo_element);
          loading.value = false;
          logo_selected_id.value = null;
        } else {
          showToast('This logo has no valid file path.');
          loading.value = false;
          logo_selected_id.value = null;
        }
      })
      .catch((errorResponse) => {
        loading.value = false;
        logo_selected_id.value = null;
      });
  };

  const handleShowLogoHistory = (id, url) => {
    // let logo_element = document.createElement('a');
    // logo_selected_id.value = id;
    showLogoHistoryDialog.value = true
    logoHistoryLoading.value = true;
    const payload = { 
      logo_url: url
    }
    http
      .post('/logoHistory', payload)
      .then((response) => {
        const response_data = response.data;
        if (response_data.success) {
          logoHistoryData.value = response_data.data
          logoHistoryLoading.value = false;
        } else {
          logoHistoryLoading.value = false;
          showLogoHistoryDialog.value = false
          showToast(response_data.message, 'error');
        }
      })
      .catch((errorResponse) => {
        showLogoHistoryDialog.value = false
        logoHistoryLoading.value = false;
        logo_selected_id.value = null;
      });
  };

  const vectorizeLogo = (logo_container, preview_logo = false, factory_product) => {
    const custom_logo = logo_container.item;
    const post_data = {
      logo_url: custom_logo.original_logo || custom_logo.original_logo_url,
      order_item_id: props.order_item_id,
      factory_product_id: factory_product.id,
      vectorizer_params: {},
      logo_index: custom_logo.logo_index,
    };
    if (preview_logo) {
      post_data.vectorizer_params['mode'] = 'preview';
    }
    let active_logo = custom_logos[logo_container.index];
    if (preview_logo) {
      custom_logo.vectorized_logo_info.show_preview_loader = true;
    } else {
      custom_logo.vectorized_logo_info.show_vectorized_loader = true;
    }
    custom_logos[logo_container.index] = active_logo;
    http
      .post('vectorize_logo', post_data)
      .then((successResponse) => {
        const response_data = successResponse.data;
        if (response_data.success) {
          const { vectorized_logo_info } = response_data.result;
          custom_logo.vectorized_logo_info = vectorized_logo_info;
          downloadVectorizedFile(logo_container.index, custom_logo, preview_logo);
        } else {
          if (preview_logo) {
            custom_logo.vectorized_logo_info.show_preview_loader = false;
          } else {
            custom_logo.vectorized_logo_info.show_vectorized_loader = false;
          }
          showToast(response_data.message, 'error');
        }
      })
      .catch((errorResponse) => {
        custom_logo.vectorized_logo_info.show_preview_loader = false;
        custom_logo.vectorized_logo_info.show_vectorized_loader = false;
        handleResponseException(errorResponse);
      });
  };
  const getDocFromString = async (
    doc_string: string,
    type: DOMParserSupportedType = 'image/svg+xml'
  ) => {
    let parser = new DOMParser();
    return parser.parseFromString(doc_string, type);
  };

  const getSVGPattern = (values, measurement_ratio) => {
    return `
                <g xmlns="
                p://www.w3.org/2000/svg" transform="matrix(1 0 0 1 0 ${5000})" style="font-weight: bold;">
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="0" y="0">Name </tspan>
                    </text>
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="${1000}" y="0">Number </tspan>
                    </text>
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="${2000}" y="0">Size </tspan>
                    </text>
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="${3000}" y="0">Name Height </tspan>
                    </text>
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="${4000}" y="0">Number Height </tspan>
                    </text>
                </g>
        ${values.map((value, index) => {
          return `
                <g xmlns="

                ://www.w3.org/2000/svg" transform="matrix(1 0 0 1 0 ${5000})">
                <g transform="matrix(${measurement_ratio ? 1 / measurement_ratio : 1} 0 0 ${measurement_ratio ? 1 / measurement_ratio : 1} 0 ${500 + index * 1000})">
                     ${value.text ? value.text : ''}
                </g>
                <g transform="matrix(${measurement_ratio ? 1 / measurement_ratio : 1} 0 0 ${measurement_ratio ? 1 / measurement_ratio : 1} 1000 ${500 + index * 1000})">
                      ${value.number ? value.number : ''}
                </g>
                <g transform="matrix(1 0 0 1 2000 ${500 + index * 1000})">
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="0" y="0">${value.size ? value.size : ''} </tspan>
                    </text>
                </g>
                <g transform="matrix(1 0 0 1 3000 ${500 + index * 1000})">
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="0" y="0">${value.name_height ? value.name_height + 'cm /' + parseFloat((Number(value.name_height) / INCH_TO_CENTIMETER).toFixed(2)) + 'in' : ''} </tspan>
                    </text>
                </g>
                <g transform="matrix(1 0 0 1 4000 ${500 + index * 1000})">
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="0" y="0">${value.number_height ? value.number_height + 'cm /' + parseFloat((Number(value.number_height) / INCH_TO_CENTIMETER).toFixed(2)) + 'in' : ''} </tspan>
                    </text>
                </g>
                ${(svg_pattern_last_value_y.value = 500 + index * 1000 + 5000)}
                </g>`;
        })}
        `;
  };
  const getLogoPattern = async (values, measurement_ratio) => {
    let svg_group_el = `
        <g xmlns="http://www.w3.org/2000/svg" transform="matrix(1 0 0 1 0 ${svg_pattern_last_value_y.value + 500})" style="font-weight: bold;">
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="0" y="0">Logo </tspan>
                    </text>
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="${1000}" y="0">Side </tspan>
                    </text>
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="${2000}" y="0">Size </tspan>
                    </text>
                </g>
       `;
    values.forEach(async (value, index) => {
      let original_url = Object.prototype.hasOwnProperty.call(value, 'original_logo_url');
      let updated_url = original_url ? value.original_logo_url : value.url;
      if (updated_url !== null && updated_url !== undefined && updated_url !== '') {
        if (getFileExtensionType('raster', updated_url)) {
          await urlToBase64(`${storage_url}/${updated_url}`).then(async (base64) => {
            svg_group_el += `
                <g xmlns="http://www.w3.org/2000/svg" transform="matrix(1 0 0 1 0 ${svg_pattern_last_value_y.value + 500})">
                <g transform="matrix(1 0 0 1 0 ${500 + parseInt(index) * 1000})">
                    ${updated_url ? `<image xlink:href="${base64}" height="${(200 * value.scaleY) / measurement_ratio}px" width="${(200 * value.scaleX) / measurement_ratio}px"/>` : ''}
                </g>
                <g transform="matrix(1 0 0 1 1000 ${500 + parseInt(index) * 1000})">
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="0" y="0">${value.side ? value.side : ''} </tspan>
                    </text>
                </g>
                <g transform="matrix(1 0 0 1 2000 ${500 + parseInt(index) * 1000})">
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="0" y="0">${value.originalWidth ? value.originalWidth + 'cm x' + value.originalHeight + 'cm /' + parseFloat((value.originalWidth / INCH_TO_CENTIMETER).toString()).toFixed(2) + 'in x' + parseFloat((value.originalHeight / INCH_TO_CENTIMETER).toString()).toFixed(2) + 'in' : ''} </tspan>
                    </text>
                </g>
                ${(logo_pattern_last_value_y.value = 500 + parseInt(index) * 1000 + (svg_pattern_last_value_y.value + 500) + 500)}
                </g>`;
          });
        } else {
          svg_group_el += `
                <g xmlns="http://www.w3.org/2000/svg" transform="matrix(1 0 0 1 0 ${svg_pattern_last_value_y.value + 500})">
                <g transform="matrix(1 0 0 1 0 ${500 + parseInt(index) * 1000})">
                    ${updated_url ? `<image xlink:href="${storage_url}/${updated_url}" height="${(200 * value.scaleY) / measurement_ratio}px" width="${(200 * value.scaleX) / measurement_ratio}px"/>` : ''}
                </g>
                <g transform="matrix(1 0 0 1 1000 ${500 + parseInt(index) * 1000})">
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="0" y="0">${value.side ? value.side : ''} </tspan>
                    </text>
                </g>
                <g transform="matrix(1 0 0 1 2000 ${500 + parseInt(index) * 1000})">
                    <text xml:space="preserve" font-family="gibson-bold-webfont" font-size="95.78" font-style="bold" paint-order="stroke">
                        <tspan x="0" y="0">${value.originalWidth ? value.originalWidth + 'cm x' + value.originalHeight + 'cm /' + parseFloat((value.originalWidth / INCH_TO_CENTIMETER).toString()).toFixed(2) + 'in x' + parseFloat((value.originalHeight / INCH_TO_CENTIMETER).toString()).toFixed(2) + 'in' : ''} </tspan>
                    </text>
                </g>
                ${(logo_pattern_last_value_y.value = 500 + parseInt(index) * 1000 + (svg_pattern_last_value_y.value + 500) + 500)}
                </g>`;
        }
      }
    });
    return svg_group_el;
  };
  const fetchUrlContent = async (url: string) => {
    // The production file lives on external storage (S3/CloudFront). It is optional:
    // it is null on many order items, and the bucket sends no CORS headers for the
    // admin origin, so fetch() rejects with "Failed to fetch". That rejection escaped
    // loadOrderDetails(), aborted the Factory Products tab before it populated, and the
    // order detail never rendered. One unreachable asset must not blank the panel.
    if (!url) return null;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.warn('[loadOrderDetails] production file returned HTTP', response.status, url);
        return null;
      }
      return await response.text();
    } catch (error) {
      console.warn('[loadOrderDetails] production file could not be fetched:', url, error);
      return null;
    }
  };
  const loadOrderDetails = async (
    selectedProduct: null | any,
    download = false,
    order: OrderDetail = null
  ) => {
    if (!selectedProduct) return;

    factoryProduct.value = selectedProduct;
    front_preview.value = selectedProduct.front_image;
    back_preview.value = selectedProduct.back_image;
    inside_front_preview.value = selectedProduct.inside_front_image ?? null;
    inside_back_preview.value = selectedProduct.inside_back_image ?? null;

    factoryProduct.value.custom_names = filter(selectedProduct.custom_texts, ['type', 'name']);
    factoryProduct.value.custom_numbers = filter(selectedProduct.custom_texts, ['type', 'number']);

    // fontFile.value = Array.isArray(activityItem.font_file) ? activityItem.font_file : []

    const content = await fetchUrlContent(factoryProduct.value.production_url);
    // `parsed` is only consumed by the download branch below; when the production file
    // is missing or unreachable we keep going so the panel still renders.
    const parsed = content
      ? await parseSvgString(content, factoryProduct.value, storage_url)
      : null;

    if (download) {
      const fileName = order ? `${order.order_no}.svg` : `${new Date().toISOString()}.svg`;
      if (factoryProduct.value.svg_url) {
        downloadProductionFile(factoryProduct.value.svg_url, fileName);
      } else if (parsed) {
        downloadFile(parsed, factoryProduct.value, order);
      } else {
        showToast('Production file is not available for this product.', 'error');
      }
    } else {
      product_texts.value = [];
      product_numbers.value = [];
      roster_detail.value = [];
      custom_logos.value = [];

      if (factoryProduct.value.product_custom_texts) {
        for (const text of factoryProduct.value.product_custom_texts) {
          const items = text.items || [];
          if (['name', 'common', 'team_name'].includes(text.type)) {
            items.forEach((item) =>
              product_texts.value.push({
                ...item,
                font_family: text.font_family,
              })
            );
          }
          if (text.type === 'number') {
            items.forEach((item) =>
              product_numbers.value.push({
                ...item,
                font_family: text.font_family,
              })
            );
          }
        }
      }

      // Roster details logic (simplified)
      const roster = factoryProduct.value.product_custom_text_objects?.roster || [];
      const common = factoryProduct.value.product_custom_text_objects?.common || [];

      roster.forEach((r, i) => {
        roster_detail.value[i] = {
          name: r.name?.items?.[0]?.svg || null,
          number: r.number?.items?.[0]?.svg || null,
          size: r.size,
          quantity: r.quantity,
        };
      });

      common.forEach((c, idx) => {
        c.items.forEach((item, i) => {
          roster_detail.value.push({
            name: item.svg,
            number: null,
            size: null,
            quantity: null,
          });
        });
      });

      if (factoryProduct.value.custom_logos?.length) {
        for (const logo of factoryProduct.value.custom_logos) {
          if (logo.id !== null) {
            logo.show_vectorized_loader = false;
            logo.show_vectorized_preview_loader = false;
            custom_logos.value.push(logo);
          }
        }
      }
    }
  };

  const downloadFile = async (production_content, selectedProduct, order) => {
    const anchorId = `production-file-${selectedProduct.product_id}`;
    let productionFileAnchor = document.getElementById(anchorId);

    if (!productionFileAnchor) {
      const anchor = document.createElement('a');
      const svgElem = await getDocFromString(production_content);

      // Assume production_file_info and related values are already declared as refs
      production_file_info.value.width =
        parseFloat(production_file_info.value.width.toString()) * 2;
      production_file_info.value.height = parseFloat(
        (logo_pattern_last_value_y
          ? logo_pattern_last_value_y
          : svg_pattern_last_value_y
        ).value.toString()
      );

      // Update SVG attributes (replacing jQuery .find and .attr)
      const svg = svgElem.querySelector('svg');
      if (svg) {
        svg.setAttribute('width', production_file_info.value.width.toString());
        svg.setAttribute('height', production_file_info.value.height.toString());
      }

      const svgContent = new XMLSerializer().serializeToString(svgElem);
      const svgName = order.order_no ? `${order.order_no}.svg` : `${getCurrentDatetime()}.svg`;

      anchor.setAttribute('id', anchorId);
      anchor.setAttribute(
        'href',
        'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgContent)
      );
      anchor.setAttribute('download', svgName);
      anchor.style.display = 'none';

      document.body.appendChild(anchor);
      anchor.click();

      // Optional: remove the anchor after download
      // document.body.removeChild(anchor);
    } else {
      productionFileAnchor.click();
    }
  };
  const getProductDetailRoster = (roster: ProductRosterDetail[]) => {
    return roster;
  };
  const getSizeSvg = async (item, product: FactoryProduct) => {
    await http.get(`${storage_url}/${product.svg_url}`).then(async (res) => {
      if (res) {
        const svg_content = await res.data?.text();
        const doc = await getDocFromString(svg_content);
        const size = item.size;
        const matchSize = doc.querySelector(`g[id="${size}"]`);
        if (matchSize) {
          const wrapperSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

          wrapperSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
          wrapperSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
          wrapperSvg.setAttribute('version', '1.1');
          wrapperSvg.setAttribute('x', '0px');
          wrapperSvg.setAttribute('y', '0px');
          wrapperSvg.setAttribute('width', '14208px');
          wrapperSvg.setAttribute('height', '8820px');
          wrapperSvg.setAttribute('viewBox', '0 0 14208 8820');
          wrapperSvg.setAttribute('enable-background', 'new 0 0 7104 7320');
          wrapperSvg.setAttribute('xml:space', 'preserve');

          wrapperSvg.appendChild(matchSize.cloneNode(true));

          const serializer = new XMLSerializer();
          const newSvgContent = serializer.serializeToString(wrapperSvg);
          const blob = new Blob([newSvgContent], {
            type: 'image/svg+xml',
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `filtered-${size}.svg`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else {
          showToast('There\'s no SVG for this size.', 'error');
        }
      }
    });
  };

  const fetchAndOpenPdf = async (order, version_id) => {
    try {
      let order_id = order.id;
      let techpack_data_length = order.techpack_data_count ? order.techpack_data_count : 0;
      loading.value = true;
      const response = await http.get(`/techpackpdf/order/${order_id}/version/${version_id}`);
      loading.value = false;
      const pdfUrl = response.data.result.pdf_url;
      if (pdfUrl) {
        window.open(`${storage_url}/${pdfUrl}`, '_blank');
      } else {
        if (techpack_data_length) {
          openV2Application(
            {
              name: '',
              to: `/order/${order_id}/condensend-pdf?techpack_version=${version_id}`,
            },
            true
          );
        } else {
          openV2Application({ name: '', to: `/order/${order_id}/condensend-pdf` }, true);
        }
      }
    } catch (error) {
      logError('Error fetching the PDF URL:', error);
    }
  };
  const fetchCompanies = async () => {
    const response = await http.get(`get-companies`);
    return response.data.result;
  };
  const handleCompanyChange = async (companyId) => {
    const response = await http.get(`manual-order-data?company_id=${companyId}`);
    return response.data.result.companies[0].customers ?? [];
  };

  const handleUpdateOrderData = async (data) => {
    try {
      const response = await http.post(`update-order-data`, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        const { message, errors } = error.response.data;

        // Build bullet list if errors exist
        let errorMessage = message || 'Something went wrong.';
        if (errors && errors.length > 0) {
          const flattened = errors.flat(); // flatten nested array
          const bulletPoints = flattened.map((e: string) => `• ${e}`).join('\n');
          errorMessage += `\n\n${bulletPoints}`;
        }

        throw new Error(errorMessage);
      }

      throw new Error('Something went wrong while updating order.');
    }
  };

  const getProductSizes = async (productId: number) => {
    try {
      const response = await http.get(`get-product-sizes/${productId}`);
      const result = response.data.data; // <-- yaha "data" use karna hai

      if (result?.sizes?.length > 0) {
        productSizes.value = result.sizes[0].json_data;
      } else {
        productSizes.value = [];
      }
    } catch (err) {
      console.error('Failed to load product sizes', err);
      productSizes.value = [];
    }
  };

  // AB#20682: delegates to the shared helper. This used to be one of two
  // copies inside this repo, and they had drifted apart.
  const makeReorderMessage = (factory_product: FactoryProduct) =>
    sharedMakeReorderMessage(factory_product as unknown as ReorderFactoryProduct);

  const getCountries = async () => {
    const response = await http.get('/addresses/countries');
    return response.data.result;
  };

  return {
    product_numbers,
    product_texts,
    custom_logos,
    logo_selected_id,
    measurement_unit,
    loading,
    roster_headers,
    custom_text_headers,
    custom_logos_headers,
    svg_group_headers,
    API_URL,
    storage_url,
    batch_loading,
    loadingPreviewImages,
    front_preview,
    back_preview,
    inside_front_preview,
    inside_back_preview,
    production_content,
    roster_detail,
    svg_pattern_last_value_y,
    logo_pattern_last_value_y,
    INCH_TO_CENTIMETER,
    computedProducts,
    batchDownloadAssets,
    downloadVectorizedFile,
    downloadImages,
    showPreview,
    downloadProductionFile,
    parseSvgString,
    downloadLogo,
    vectorizeLogo,
    getDocFromString,
    getSVGPattern,
    getLogoPattern,
    loadOrderDetails,
    getProductDetailRoster,
    getSizeSvg,
    fetchAndOpenPdf,
    fetchCompanies,
    handleCompanyChange,
    handleUpdateOrderData,
    getProductSizes,
    makeReorderMessage,
    isEditing,
    backupData,
    productSizes,
    getCountries,
    logo_history_headers,
    showLogoHistoryDialog,
    logoHistoryLoading,
    logoHistoryData
  };
};
